import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useRecordingSession — the single shared recording engine for notes-v2.
 *
 * The mic capture chain, WebSocket transport, mic enumeration/permission, the
 * audio level + mute detector, the recording timer and the pause/resume/End-Note
 * state machine were MOVED here verbatim from App.js (they were previously inline
 * in App.js and copied in a stripped form into WidgetPage.tsx). App-specific
 * note/summary/coding UI state stays in the consumer, driven off the raw
 * `onMessage` tap and the onOpen/onError/onClose lifecycle callbacks.
 *
 * State machine (unchanged from App.js):
 *   - start()   → fresh session (new id unless supplied), timer 0, buffers cleared.
 *   - pause()   → intentional WS close, NO end_session; server keeps the buffer;
 *                 timer freezes; connState → 'paused'.
 *   - resume()  → reconnect with the SAME session id; timer continues.
 *   - endNote() → send {type:'end_session'}, stop capture immediately (mic off),
 *                 then close the WS. Default (main app) closes on end_session_ack
 *                 (≤3s fallback) and the consumer HTTP-polls the persisted note.
 *                 With `waitForFinalSummary` (widget) the WS is held open until the
 *                 post-end `summary` arrives (≤120s) before closing. Resolves once
 *                 the socket has closed.
 *
 * The old 30s keepalive ping was intentionally dropped in the move (nginx /ws/
 * proxy uses proxy_read_timeout 86400; the backend ping-skip is harmless).
 *
 * Auto-reconnect (2026-09-17): an UNINTENTIONAL close (Wi-Fi blip, laptop
 * sleep, proxy reset) while a session id exists resumes the same session on
 * its own — RECONNECT_DELAYS_MS attempts, then it surrenders to the consumer
 * as before ('disconnected' + onClose{intentional:false}). During the retries
 * connState is 'connecting' and onClose fires with {reconnecting:true,
 * attempt} so the consumer can say so instead of showing an error. pause()
 * and endNote() are intentional and never reconnect.
 */
const RECONNECT_DELAYS_MS = [1000, 3000, 6000];

export function useRecordingSession(options = {}) {
    // Config the WS handlers read at connect time (avoids stale-closure params).
    const cfgRef = useRef({});
    cfgRef.current = {
        token: options.token,
        wsUrl: options.wsUrl,
        mode: options.mode || 'none',
        model: options.model || '',
        reportName: options.reportName || 'SOAP_Chart',
        language: options.language || '',
        fhirPatientId: options.fhirPatientId || '',
        fhirEncounterId: options.fhirEncounterId || '',
        coding: options.coding,
        clientRef: options.clientRef || '',
        waitForFinalSummary: !!options.waitForFinalSummary,
    };

    // Callbacks read through a ref so WS handlers always call the latest closures
    // without re-creating the transport on every render.
    const cbRef = useRef({});
    cbRef.current = {
        onOpen: options.onOpen,
        onMessage: options.onMessage,
        onError: options.onError,
        onClose: options.onClose,
    };

    // ── Public state ──────────────────────────────────────────────────────
    const [connState, setConnState] = useState('idle'); // idle|connecting|connected|paused|ending|disconnected|error
    const [transcript, setTranscript] = useState([]);   // string[] (simple view for the widget)
    const [report, setReport] = useState('');           // latest summary (simple view)
    const [sessionId, setSessionId] = useState('');
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [micPermission, setMicPermission] = useState('unknown'); // unknown|granted|denied|prompt
    const [mics, setMics] = useState([]);
    const [selectedMicId, setSelectedMicId] = useState('');

    // ── Internal transport / capture refs (moved from App.js) ─────────────
    const websocketRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const analyserRef = useRef(null);
    const muteFramesRef = useRef(0);
    const timerIntervalRef = useRef(null);
    const intentionalCloseRef = useRef(false);
    const endRequestedRef = useRef(false);
    const closeTimerRef = useRef(null);
    const endNoteResolveRef = useRef(null);
    // True once the backend confirmed this End Note (end_session_ack, or the
    // post-end summary). endNote() resolves with THIS, not with "send didn't
    // throw": a connection dying right at End Note must resolve false so the
    // consumer falls back to HTTP finalize instead of fetching a stale note.
    const endAckedRef = useRef(false);
    const sessionIdRef = useRef('');
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    // Audio captured while no socket is OPEN (handshake, reconnect backoff).
    const pendingAudioRef = useRef([]);
    // connect() is referenced from its own onclose handler (reconnect); a ref
    // avoids the self-dependency in useCallback.
    const connectRef = useRef(null);
    // True from connect() entry until the socket settles (open/close/error).
    // A double-clicked Start/Resume (or a user Resume racing a reconnect
    // attempt) otherwise opens TWO capture chains sending into one socket —
    // doubled interleaved audio — and leaks the first MediaStream (mic stays
    // hot until the page closes).
    const connectingGuardRef = useRef(false);
    // Latest mic selection/permission for connect() without re-creating it.
    const micPermissionRef = useRef('unknown');
    const selectedMicIdRef = useRef('');
    micPermissionRef.current = micPermission;
    selectedMicIdRef.current = selectedMicId;

    // ~10s of 4096-frame chunks at 48kHz. Oldest chunks are dropped first, so a
    // socket that never comes back costs memory, not correctness.
    const MAX_PENDING_AUDIO_CHUNKS = 120;

    const flushPendingAudio = useCallback((ws) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        while (pendingAudioRef.current.length) {
            const chunk = pendingAudioRef.current.shift();
            try { ws.send(chunk); } catch { break; }
        }
    }, []);

    // ── enumerateMics (moved verbatim from App.js) ────────────────────────
    const enumerateMics = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioIn = devices.filter(d => d.kind === 'audioinput');
            setMics(audioIn);
            // If labels are empty, permission hasn't been granted yet
            if (audioIn.length > 0 && audioIn[0].label) {
                setMicPermission('granted');
            }
            // Keep selected mic valid
            setSelectedMicId(prev => {
                if (prev && audioIn.find(m => m.deviceId === prev)) return prev;
                return audioIn[0]?.deviceId || '';
            });
        } catch {
            // ignore
        }
    }, []);

    // Mic permission + device-change wiring (moved verbatim from App.js)
    useEffect(() => {
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'microphone' }).then(status => {
                setMicPermission(status.state); // 'granted'|'denied'|'prompt'
                status.onchange = () => {
                    setMicPermission(status.state);
                    if (status.state === 'granted') enumerateMics();
                };
            }).catch(() => {});
        }
        enumerateMics();
        navigator.mediaDevices.addEventListener('devicechange', enumerateMics);
        return () => navigator.mediaDevices.removeEventListener('devicechange', enumerateMics);
    }, [enumerateMics]);

    const selectMic = useCallback((id) => setSelectedMicId(id), []);

    // ── Recording timer (moved from App.js; keyed on connState) ───────────
    useEffect(() => {
        if (connState === 'connected') {
            timerIntervalRef.current = setInterval(() => {
                setRecordingSeconds(s => s + 1);
            }, 1000);
        } else {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [connState]);

    // ── convertFloat32ToInt16 (moved verbatim from App.js) ────────────────
    const convertFloat32ToInt16 = (buffer) => {
        const int16Array = new Int16Array(buffer.length);
        for (let i = 0; i < buffer.length; i++) {
            const s = Math.max(-1, Math.min(1, buffer[i]));
            int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return int16Array;
    };

    // ── ensureSessionId / generateSessionId (moved verbatim from App.js) ──
    const generateSessionId = () => {
        try {
            if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                return window.crypto.randomUUID();
            }
        } catch (e) {
            // ignore
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    // Stop capturing (mic off) without touching the WS. Consolidates the
    // teardown that App.js/WidgetPage did in stopSession/releaseAudioCapture.
    const releaseAudioCapture = useCallback(() => {
        if (processorRef.current) {
            try { processorRef.current.disconnect(); } catch { /* already gone */ }
            processorRef.current = null;
        }
        if (audioContextRef.current) {
            try { audioContextRef.current.close(); } catch { /* already gone */ }
            audioContextRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        analyserRef.current = null;
        muteFramesRef.current = 0;
        setAudioLevel(0);
        setIsMuted(false);
    }, []);

    // Close the live socket, marking it intentional so onclose doesn't surface
    // a "connection lost" error.
    const closeSocketNow = useCallback(() => {
        if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
        const ws = websocketRef.current;
        if (ws) {
            try { ws.close(); } catch { /* already closing */ }
        }
    }, []);

    // ── connect: the startSession audio+WS block, moved from App.js ───────
    const connect = useCallback(async (sessionId) => {
        const cfg = cfgRef.current;

        // Get microphone access (moved verbatim from App.js)
        const audioConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
        };
        // Only apply deviceId constraint after permission is granted (labels visible = real IDs)
        if (selectedMicIdRef.current && micPermissionRef.current === 'granted') {
            audioConstraints.deviceId = { exact: selectedMicIdRef.current };
        }

        if (connectingGuardRef.current) {
            console.warn('connect() ignored — another connect is already in flight');
            return;
        }
        connectingGuardRef.current = true;

        setConnState('connecting');

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
        } catch (err) {
            connectingGuardRef.current = false;
            setConnState('error');
            releaseAudioCapture();
            throw err; // consumer maps NotAllowedError / NotFoundError to a message
        }
        // Permission granted — refresh device list to get labels
        setMicPermission('granted');
        enumerateMics();

        // Pause or End Note pressed while the mic prompt/acquisition was in
        // flight (a reconnect attempt can start one without the user asking):
        // honour it instead of coming back online behind their back.
        if (intentionalCloseRef.current) {
            connectingGuardRef.current = false;
            stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* already stopped */ } });
            setConnState(endRequestedRef.current ? 'disconnected' : 'paused');
            return;
        }

        mediaStreamRef.current = stream;

        // Setup audio context (moved verbatim from App.js — resume if suspended).
        // Assign the ref BEFORE the resume() await: if anything bails in that
        // window, releaseAudioCapture can still close this context (browsers
        // cap the number of live AudioContexts).
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 48000
        });
        audioContextRef.current = audioContext;
        if (audioContext.state !== 'running') {
            console.log(`🔊 AudioContext state: ${audioContext.state} — resuming`);
            await audioContext.resume();
        }

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        // Create analyzer for visualizer
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;
        source.connect(analyser);

        // Do NOT reset intentionalCloseRef/endRequestedRef here: start() and
        // resume() already clear them for user-initiated connects, and a
        // reconnect keeps them meaningful. Clearing them unconditionally let a
        // Pause/End pressed during the audioContext await above be silently
        // overridden — the mic then kept recording into a finalized session.
        // Instead, honour a Pause/End that landed while we were setting up:
        if (intentionalCloseRef.current) {
            connectingGuardRef.current = false;
            releaseAudioCapture();
            setConnState(endRequestedRef.current ? 'disconnected' : 'paused');
            return;
        }

        // Connect WebSocket (params unified from App.js + WidgetPage.tsx)
        const params = new URLSearchParams();
        params.set('token', cfg.token || '');
        params.set('mode', cfg.mode || 'none');
        if (cfg.model) params.set('model', cfg.model);
        params.set('session_id', sessionId);
        params.set('report_name', cfg.reportName || '');
        params.set('language', cfg.language || '');
        if (cfg.fhirPatientId) params.set('fhir_patient_id', cfg.fhirPatientId);
        if (cfg.fhirEncounterId) params.set('fhir_encounter_id', cfg.fhirEncounterId);
        if (cfg.coding !== undefined && cfg.coding !== null) params.set('coding', cfg.coding);
        // Host record this recording belongs to; the backend stores it and
        // delivers the finished note to it without needing this page.
        if (cfg.clientRef) params.set('client_ref', cfg.clientRef);

        const ws = new WebSocket(`${cfg.wsUrl}?${params.toString()}`);
        websocketRef.current = ws;

        // Capture audio from the moment the mic is live — NOT from ws.onopen.
        // Speech during the WS handshake (~1-3s, e.g. talking right after
        // pressing Resume) is buffered and flushed once the socket opens,
        // instead of being silently dropped. The buffer lives OUTSIDE connect()
        // (pendingAudioRef) so it also covers the auto-reconnect wait: a drop
        // mid-sentence keeps recording into the buffer for the 1s/3s/6s backoff
        // and the handshake that follows, instead of losing those seconds.
        // Capped at ~10s so a connect that never succeeds can't grow it
        // unbounded — the oldest audio is dropped first.
        processor.onaudioprocess = (e) => {
            if (endRequestedRef.current) return;
            const inputData = e.inputBuffer.getChannelData(0);
            const pcm16 = convertFloat32ToInt16(inputData);

            const live = websocketRef.current;
            if (live && live.readyState === WebSocket.OPEN) {
                flushPendingAudio(live);
                live.send(pcm16.buffer);
            } else {
                pendingAudioRef.current.push(pcm16.buffer);
                if (pendingAudioRef.current.length > MAX_PENDING_AUDIO_CHUNKS) {
                    pendingAudioRef.current.shift();
                }
            }

            // Calculate audio level for visualizer and mute detection
            let sum = 0;
            for (let i = 0; i < inputData.length; i++) {
                sum += inputData[i] * inputData[i];
            }
            const rms = Math.sqrt(sum / inputData.length);
            setAudioLevel(rms);
            // Mute detection: ~30 consecutive silent frames ≈ 0.5s at 4096/48000
            if (rms < 0.002) {
                muteFramesRef.current += 1;
                if (muteFramesRef.current >= 30) setIsMuted(true);
            } else {
                muteFramesRef.current = 0;
                setIsMuted(false);
            }
        };
        source.connect(processor);
        processor.connect(audioContext.destination);

        ws.onopen = () => {
            connectingGuardRef.current = false;
            console.log(`✅ WebSocket connected (${pendingAudioRef.current.length} buffered audio chunks)`);
            setConnState('connected');
            intentionalCloseRef.current = false;
            reconnectAttemptsRef.current = 0;
            try { cbRef.current.onOpen?.(); } catch (e) { console.error(e); }

            // Flush speech captured during the handshake (and, after a drop,
            // during the reconnect wait) right away.
            flushPendingAudio(ws);
        };

        ws.onmessage = (event) => {
            let message;
            try { message = JSON.parse(event.data); } catch { return; }

            // Transport-level handling (identity, simple views, End-Note close cues)
            switch (message.type) {
                case 'connected':
                    if (message.session_id) {
                        sessionIdRef.current = message.session_id;
                        setSessionId(message.session_id);
                    }
                    break;
                case 'transcript':
                    setTranscript(prev => [...prev, `${message.speaker}: ${message.content}`]);
                    break;
                case 'summary':
                    setReport(message.content || '');
                    // Post-end final summary → close (widget flow).
                    if (endRequestedRef.current && cfgRef.current.waitForFinalSummary) {
                        endAckedRef.current = true;
                        closeSocketNow();
                    }
                    break;
                case 'end_session_ack':
                    if (endRequestedRef.current) endAckedRef.current = true;
                    // Backend set finalize_requested — safe to close now (app flow).
                    if (endRequestedRef.current && !cfgRef.current.waitForFinalSummary) {
                        closeSocketNow();
                    }
                    break;
                default:
                    break;
            }

            // Raw tap: the consumer drives its rich note/summary/coding state here.
            try { cbRef.current.onMessage?.(message); } catch (e) { console.error(e); }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setConnState('error');
            try { cbRef.current.onError?.(error); } catch (e) { console.error(e); }
        };

        ws.onclose = (event) => {
            connectingGuardRef.current = false;
            console.log(`WebSocket closed — code=${event.code} reason="${event.reason || '(none)'}" wasClean=${event.wasClean}`);
            if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
            releaseAudioCapture();

            const wasEndNote = endRequestedRef.current;
            const wasIntentional = intentionalCloseRef.current || wasEndNote;

            // Unintentional drop mid-session → resume the same session id on
            // our own before bothering the user (see header comment).
            if (!wasIntentional && sessionIdRef.current
                && reconnectAttemptsRef.current < RECONNECT_DELAYS_MS.length) {
                const attempt = reconnectAttemptsRef.current + 1;
                reconnectAttemptsRef.current = attempt;
                const delay = RECONNECT_DELAYS_MS[attempt - 1];
                console.warn(`⚠️ Unexpected disconnect (code ${event.code}) — reconnect attempt ${attempt}/${RECONNECT_DELAYS_MS.length} in ${delay}ms`);
                websocketRef.current = null;
                endRequestedRef.current = false;
                setConnState('connecting');
                try {
                    cbRef.current.onClose?.({
                        intentional: false, endNote: false, code: event.code, reason: event.reason,
                        reconnecting: true, attempt, maxAttempts: RECONNECT_DELAYS_MS.length,
                    });
                } catch (e) { console.error(e); }
                reconnectTimerRef.current = setTimeout(() => {
                    reconnectTimerRef.current = null;
                    const sid = sessionIdRef.current;
                    if (!sid || intentionalCloseRef.current) return;
                    Promise.resolve(connectRef.current?.(sid)).catch((err) => {
                        // Mic or socket setup failed before onclose could run:
                        // count it as a failed attempt and go around again.
                        console.warn('Reconnect attempt failed:', err);
                        ws.onclose?.({ code: 4999, reason: 'reconnect failed', wasClean: false });
                    });
                }, delay);
                return;
            }

            if (wasEndNote) setConnState('disconnected');
            else if (intentionalCloseRef.current) setConnState('paused');
            else setConnState('disconnected');

            try {
                cbRef.current.onClose?.({
                    intentional: wasIntentional,
                    endNote: wasEndNote,
                    code: event.code,
                    reason: event.reason,
                });
            } catch (e) { console.error(e); }

            endRequestedRef.current = false;
            websocketRef.current = null;

            if (endNoteResolveRef.current) {
                const resolve = endNoteResolveRef.current;
                endNoteResolveRef.current = null;
                resolve();
            }
        };
    }, [enumerateMics, releaseAudioCapture, closeSocketNow]);
    connectRef.current = connect;

    // ── Actions ────────────────────────────────────────────────────────────

    // Fresh session: new id (unless supplied), timer 0, cleared buffers.
    const start = useCallback(async (sid) => {
        const id = sid || generateSessionId();
        sessionIdRef.current = id;
        setSessionId(id);
        setTranscript([]);
        setReport('');
        setRecordingSeconds(0);
        muteFramesRef.current = 0;
        pendingAudioRef.current = [];
        // The user asked for this connection — clear the flag a previous pause
        // or End Note left set (connect() aborts on it).
        intentionalCloseRef.current = false;
        endRequestedRef.current = false;
        await connect(id);
        return id;
    }, [connect]);

    // Reconnect with the SAME session id; timer / transcript continue.
    const resume = useCallback(async (sid) => {
        const id = sid || sessionIdRef.current;
        if (!id) return start();
        // Same session → keep any audio buffered during a reconnect surrender
        // (that buffer exists precisely so those seconds of speech aren't
        // lost). A DIFFERENT session must never inherit another's audio.
        if (id !== sessionIdRef.current) pendingAudioRef.current = [];
        sessionIdRef.current = id;
        setSessionId(id);
        intentionalCloseRef.current = false;
        endRequestedRef.current = false;
        await connect(id);
        return id;
    }, [connect, start]);

    // Drop the note text the hook is holding (the consumer reopened the visit
    // and must not keep showing the previous version once a new one lands).
    const clearNote = useCallback(() => setReport(''), []);

    // Intentional close, no end_session. Buffer preserved server-side.
    const pause = useCallback(() => {
        intentionalCloseRef.current = true;
        endRequestedRef.current = false;
        if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
        reconnectAttemptsRef.current = 0;
        closeSocketNow();
        // A pause during a reconnect wait has no socket to close: settle the state here.
        if (!websocketRef.current) setConnState('paused');
    }, [closeSocketNow]);

    // End Note: flush + finalize, then close. Resolves once the socket closes,
    // with `true` when the end ran over the WS (final summary awaited). If the
    // socket isn't open (paused), resolves `false` — the consumer finalizes
    // over HTTP. Pressed while still CONNECTING (user talks fast after
    // Resume): wait for the socket to open so the buffered audio and
    // end_session take the normal WS path — resolving early would let the
    // consumer fetch the note before the backend regenerates it.
    const endNote = useCallback(async () => {
        // Ending is intentional: kill any pending auto-reconnect first.
        // Without this, End Note pressed during a reconnect wait finalized the
        // visit over HTTP and THEN let the timer fire, re-acquiring the mic and
        // recording into a session the backend had already finished.
        if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
        reconnectAttemptsRef.current = 0;
        intentionalCloseRef.current = true;

        let ws = websocketRef.current;
        if (ws && ws.readyState === WebSocket.CONNECTING) {
            // Bounded: a socket that never settles must not hang the End Note
            // button on "Generating the final note…" forever — after the wait
            // we fall through to the HTTP path like any closed socket.
            await new Promise((settled) => {
                const timer = setTimeout(settled, 10000);
                const settle = () => { clearTimeout(timer); settled(); };
                ws.addEventListener('open', settle, { once: true });
                ws.addEventListener('close', settle, { once: true });
                ws.addEventListener('error', settle, { once: true });
            });
            ws = websocketRef.current;
        }
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            return false;
        }
        return new Promise((resolve) => {
            // Resolve with whether the backend actually acknowledged the end
            // (see endAckedRef) — not with whether we managed to call send().
            endAckedRef.current = false;
            endNoteResolveRef.current = () => resolve(endAckedRef.current);
            endRequestedRef.current = true;
            intentionalCloseRef.current = true;
            setConnState('ending');
            releaseAudioCapture(); // mic indicator off immediately

            try {
                ws.send(JSON.stringify({ type: 'end_session' }));
            } catch (e) {
                console.warn('Failed to send end_session:', e);
                closeSocketNow();
                return;
            }

            // Fallback close: app flow waits ≤3s for end_session_ack; widget flow
            // waits ≤120s for the post-end summary (near-silent sessions never
            // send one). onmessage closes earlier when its cue arrives.
            const timeout = cfgRef.current.waitForFinalSummary ? 120000 : 3000;
            closeTimerRef.current = setTimeout(() => { closeSocketNow(); }, timeout);
        });
    }, [closeSocketNow, releaseAudioCapture]);

    // Send an arbitrary JSON message on the live socket (e.g. user_edit).
    const send = useCallback((obj) => {
        const ws = websocketRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify(obj)); return true; } catch { return false; }
        }
        return false;
    }, []);

    // Unmount cleanup
    useEffect(() => {
        return () => {
            intentionalCloseRef.current = true;
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            try { websocketRef.current?.close(); } catch { /* already gone */ }
            releaseAudioCapture();
        };
    }, [releaseAudioCapture]);

    return {
        // state
        connState,
        transcript,
        report,
        sessionId,
        recordingSeconds,
        audioLevel,
        isMuted,
        micPermission,
        mics,
        selectedMicId,
        analyser: analyserRef.current,
        // actions
        start,
        resume,
        pause,
        endNote,
        clearNote,
        selectMic,
        send,
        setRecordingSeconds, // consumers seed this when displaying a past session
    };
}

export default useRecordingSession;
